import {
  childrenKeyError,
  DEFAULT_PROPERTY_KEY,
  type FieldKeyError,
  fieldKeyError,
  namedFieldKeyError,
} from './field-key';
import type { Bundle, FieldType, Property } from './types';
import { INPUT_NODE_ID, isDecisionNode, isManagedNode, isTaskNode } from './types';

// CodeQL flags any id derived from `Math.random` as a security defect, however local the id is.
export const uid = (prefix: string): string => {
  const [random] = crypto.getRandomValues(new Uint32Array(1));
  return `${prefix}_${random.toString(36)}`;
};

/**
 * Born with a valid key, never blank: `childrenToObjectSchema` drops a blank-keyed property and
 * `nodeKeyError` stops the whole node from saving, so an unnamed row costs the author their other edits.
 */
export const newProperty = (siblings: Property[] = []): Property => ({
  id: uid('prop'),
  name: availableKey(DEFAULT_PROPERTY_KEY, new Set(siblings.map(sibling => sibling.name))),
  type: 'text',
});

export const availableKey = (base: string, taken: Set<string>): string => {
  if (!taken.has(base)) return base;
  let suffix = 2;
  while (taken.has(`${base}_${suffix}`)) suffix += 1;
  return `${base}_${suffix}`;
};

const MIN_ENUM_VALUES = 2;

export const padEnumValues = (values: string[] | undefined): string[] => {
  const list = values ?? [];
  if (list.length >= MIN_ENUM_VALUES) return list;
  return [...list, ...Array(MIN_ENUM_VALUES - list.length).fill('')];
};

// Single source of truth for the nav-row / canvas `data-id` scheme, so minting (nav model,
// field/artifact rows) and parsing (canvas nav-jump auto-expand) can't drift apart.
const FIELD_ROW_PREFIX = 'field-';
const ARTIFACT_ROW_PREFIX = 'artifact-';

export const fieldRowId = (fieldId: string): string => `${FIELD_ROW_PREFIX}${fieldId}`;
export const artifactRowId = (artifactId: string): string => `${ARTIFACT_ROW_PREFIX}${artifactId}`;

/** Inverse of {@link fieldRowId} / {@link artifactRowId}: resolves a target id to its child. */
export const parseChildRowId = (rowId: string): { kind: 'field' | 'artifact'; id: string } | null => {
  if (rowId.startsWith(FIELD_ROW_PREFIX)) return { kind: 'field', id: rowId.slice(FIELD_ROW_PREFIX.length) };
  if (rowId.startsWith(ARTIFACT_ROW_PREFIX)) return { kind: 'artifact', id: rowId.slice(ARTIFACT_ROW_PREFIX.length) };
  return null;
};

type KeyedRow = { id: string; name: string; type: FieldType | null; children?: Property[] };
type KeyError = { nodeId: string; target: string; key: string; error: FieldKeyError };

const badKeyRows = (rows: KeyedRow[]) =>
  rows.flatMap(row => {
    const error = namedFieldKeyError(row, rows) ?? childrenKeyError(row);
    return error ? [{ row, error }] : [];
  });

// Nested properties have no scroll anchor, so a bad one points at its top-level row. A task field's id
// is its saved key, which a pasted task shares, so a shared id points at the task instead.
export const keyErrors = (bundle: Bundle): KeyError[] => {
  const taskFieldIds = bundle.nodes.flatMap(node => {
    const task = isManagedNode(node) ? node.task : isTaskNode(node) ? node : null;
    return task ? task.fields.map(field => field.id) : [];
  });
  const sharedIds = new Set(taskFieldIds.filter((id, index) => taskFieldIds.indexOf(id) !== index));
  return [
    ...badKeyRows(bundle.inputs).map(({ row, error }) => ({
      nodeId: INPUT_NODE_ID,
      target: fieldRowId(row.id),
      key: row.name,
      error,
    })),
    ...bundle.nodes.flatMap((node): KeyError[] => {
      if (isManagedNode(node)) return [];
      const nodeTarget = `${node.kind}-${node.id}`;
      if (isTaskNode(node)) {
        return badKeyRows(node.fields).map(({ row, error }) => ({
          nodeId: node.id,
          target: sharedIds.has(row.id) ? nodeTarget : fieldRowId(row.id),
          key: row.name,
          error,
        }));
      }
      if (isDecisionNode(node)) {
        const questions = node.questions.map(question => ({ id: question.id, name: question.key, type: null }));
        return badKeyRows(questions).map(({ row, error }) => ({
          nodeId: node.id,
          target: fieldRowId(row.id),
          key: row.name,
          error,
        }));
      }
      const error = fieldKeyError(node.key);
      return error ? [{ nodeId: node.id, target: nodeTarget, key: node.key, error }] : [];
    }),
  ];
};

/** Criteria whose value is gone or whose text was cleared are dropped: nothing on screen could reach them. */
export const publishedCriteria = (criteria: Record<string, string>, values: string[]): Record<string, string> => {
  const allowed = new Set(values.map(value => value.trim()).filter(Boolean));
  return Object.fromEntries(Object.entries(criteria).filter(([value, text]) => allowed.has(value) && text.trim()));
};
